import { supabase } from '../clients/supabaseClient';
import type { Delivery, Driver, OrderWithActiveDelivery } from '../types';
import { ORDER_STATUS } from '../constants/orderStatuses';
import { unwrapSupabaseJoin } from '../utils/supabase';

// ── Types ──────────────────────────────────────────────────────────────────────

export type DeliveryOption = {
    id: string;
    type: string;
    amount: number;
    valid_from: string;
    valid_to: string | null;
};

export type FreeDeliveryConfig = {
    id: string;
    threshold_cents: number;
    is_active: boolean;
};

export type NewDeliveryPayload = {
    order_id: string;
    customer_id: string | number;
    destination_id: string;
    driver_id: string;
    status: string;
    status_id?: string;
};

export type ActiveDriverDeliveryDestination = {
    driver_id: string;
    status: string;
    addresses: {
        latitude: number | null;
        longitude: number | null;
    } | null;
};

// ── Delivery options ───────────────────────────────────────────────────────────

export async function fetchActiveDeliveryOptionsFromDb(): Promise<DeliveryOption[]> {
    const now = new Date().toISOString();
    const { data, error } = await supabase
        .from('delivery_options')
        .select('*')
        .or(`valid_to.is.null,valid_to.gt.${now}`)
        .order('type', { ascending: true });

    if (error) throw error;
    return (data ?? []) as DeliveryOption[];
}

export async function insertDeliveryOptionsInDb(
    options: { type: string; amount: number; valid_from: string; valid_to?: string }[]
): Promise<void> {
    const { error } = await supabase.from('delivery_options').insert(options);
    if (error) throw error;
}

export async function archiveDeliveryOptionInDb(id: string): Promise<void> {
    const { error } = await supabase
        .from('delivery_options')
        .update({ valid_to: new Date().toISOString() })
        .eq('id', id);
    if (error) throw error;
}

// ── Free delivery config ───────────────────────────────────────────────────────

export async function fetchFreeDeliveryConfigFromDb(): Promise<FreeDeliveryConfig | null> {
    const { data, error } = await supabase
        .from('free_delivery_config')
        .select('*')
        .eq('is_active', true)
        .single();

    if (error && error.code !== 'PGRST116') throw error;
    return (data as FreeDeliveryConfig) ?? null;
}

export async function upsertFreeDeliveryConfigInDb(
    existingId: string | null,
    threshold_cents: number
): Promise<void> {
    if (existingId) {
        const { error } = await supabase
            .from('free_delivery_config')
            .update({ threshold_cents, updated_at: new Date().toISOString() })
            .eq('id', existingId);
        if (error) throw error;
    } else {
        const { error } = await supabase
            .from('free_delivery_config')
            .insert({ threshold_cents, is_active: true });
        if (error) throw error;
    }
}

export async function toggleFreeDeliveryInDb(id: string, is_active: boolean): Promise<void> {
    const { error } = await supabase
        .from('free_delivery_config')
        .update({ is_active, updated_at: new Date().toISOString() })
        .eq('id', id);
    if (error) throw error;
}

// ── Deliveries ─────────────────────────────────────────────────────────────────

export async function createDeliveryInDb(delivery: NewDeliveryPayload) {
    const { data, error } = await supabase
        .from('deliveries')
        .insert([{
            ...delivery,
            assigned_at:  new Date().toISOString(),
            last_updated: new Date().toISOString(),
        }])
        .select()
        .single();

    if (error) throw error;
    return data;
}

export async function fetchDeliveriesFromDb(): Promise<Delivery[]> {
    const { data, error } = await supabase
        .from('deliveries')
        .select(`
            *,
            driver:drivers(*),
            customer:customers(*),
            order:orders(*),
            destination:addresses(id, latitude, longitude, address_1)
        `)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as Delivery[];
}

export async function fetchActiveDriverDeliveryDestinationsFromDb(
    drivers: Pick<Driver, 'auth_id'>[]
): Promise<ActiveDriverDeliveryDestination[]> {
    const driverIds = drivers.map(d => d.auth_id).filter(Boolean);
    if (driverIds.length === 0) return [];

    const { data, error } = await supabase
        .from('deliveries')
        .select(`
            driver_id,
            status,
            addresses:destination_id ( latitude, longitude )
        `)
        .in('driver_id', driverIds)
        .eq('status', ORDER_STATUS.DRIVER_ASSIGNED);

    if (error) throw error;

    return (data ?? []).map(row => {
        const address = unwrapSupabaseJoin(row.addresses as { latitude: number | null; longitude: number | null } | { latitude: number | null; longitude: number | null }[] | null);
        return {
            driver_id: String(row.driver_id),
            status:    String(row.status),
            addresses: address
                ? {
                    latitude:  address.latitude  === null ? null : Number(address.latitude),
                    longitude: address.longitude === null ? null : Number(address.longitude),
                }
                : null,
        };
    });
}

export async function fetchTotalDeliveryCountFromDb(): Promise<number> {
    const { count, error } = await supabase
        .from('deliveries')
        .select('*', { count: 'exact', head: true });

    if (error) throw error;
    return count ?? 0;
}

export async function fetchOrdersWithActiveDeliveriesFromDb(): Promise<OrderWithActiveDelivery[]> {
    const { data, error } = await supabase
        .from('orders_with_driver_details')
        .select('*')
        .order('assigned_date', { ascending: false });

    if (error) throw error;
    return (data ?? []) as OrderWithActiveDelivery[];
}

// ── Driver assignment ──────────────────────────────────────────────────────────

async function fetchStatusIdLocal(name: string): Promise<string | undefined> {
    const { data } = await supabase
        .from('statuses')
        .select('id')
        .eq('name', name)
        .single();
    return data?.id;
}

export async function assignDriverToOrderInDb(
    orderId: string,
    customerId: string | number,
    destinationId: string,
    driverId: string,
): Promise<boolean> {
    const statusId = await fetchStatusIdLocal(ORDER_STATUS.DRIVER_ASSIGNED);
    if (!statusId) return false;

    const delivery = await createDeliveryInDb({
        order_id:       orderId,
        customer_id:    customerId,
        destination_id: destinationId,
        driver_id:      driverId,
        status:         ORDER_STATUS.DRIVER_ASSIGNED,
        status_id:      statusId,
    });

    if (!delivery) return false;

    const { data: latestLog } = await supabase
        .from('order_status_history')
        .select('id')
        .eq('order_id', orderId)
        .eq('status_id', statusId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

    if (latestLog) {
        await supabase.from('order_status_history')
            .update({ model_id: driverId, model_type: 'Driver' })
            .eq('id', latestLog.id);
    }

    await supabase.from('orders')
        .update({ status: ORDER_STATUS.DRIVER_ASSIGNED, status_id: statusId })
        .eq('id', orderId);

    return true;
}

export async function unassignDriverFromOrderInDb(orderId: string | number): Promise<void> {
    const statusId = await fetchStatusIdLocal(ORDER_STATUS.ASSIGN_DRIVER);
    const { error } = await supabase
        .from('deliveries')
        .update({ driver_id: null, status: ORDER_STATUS.ASSIGN_DRIVER, status_id: statusId })
        .eq('order_id', orderId);

    if (error) throw error;
}

export async function fetchDeliveriesForOrderFromDb(orderId: string) {
    const { data, error } = await supabase
        .from('deliveries')
        .select('*, driver:drivers(*)')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data ?? [];
}

export async function fetchDriverLocationsForDeliveryFromDb(deliveryId: string) {
    const { data, error } = await supabase
        .from('driver_locations')
        .select('*')
        .eq('delivery_id', deliveryId);

    if (error) throw error;
    return data ?? [];
}

export async function fetchActiveDeliveriesFromDb(): Promise<Delivery[]> {
    const { data, error } = await supabase
        .from('deliveries')
        .select(`
            *,
            driver:drivers(*),
            customer:customers(*),
            order:orders(*),
            destination:addresses(*)
        `)
        .not('status', 'in', '("DELIVERED", "CANCELLED")')
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as Delivery[];
}